import type {
	CancelCondition,
	ChatMessage,
	CreateOccurrencePayload,
	DeleteOccurrencesPayload,
	EventDetailData,
	EventOccurrence,
	ManualReservationPayload,
	MemberDetail,
	Registration,
	ReservationExportParams,
	UpdateAttendancePayload,
	UpdateOccurrencePayload,
	UpdateRegistrationPayload,
	UpdateRegistrationStatusPayload,
} from "../types";

const API_BASE = "/api/m/event";

interface OccasionApiResponse {
	occasion: {
		eventOccasionId: number;
		title: string;
		subtitle?: string | null;
		description?: string | null;
		heroImageUrl?: string | null;
		gallery?: string[];
		isPublished: boolean;
		tags?: string[];
		fee?: number;
		price?: string | number | null;
		cancelable?: boolean;
		isMultiEvent?: boolean;
		allowMultipleBookings?: boolean;
		isMessage?: boolean;
		isCheckin?: boolean;
		membershipScope?: "member_only" | "member_and_guest";
		messageRegisteredLineWorks?: string | null;
		messageCanceledLineWorks?: string | null;
		emailsLineWorks?: string[];
		adminNotificationMemberIds?: string | number[];
		categoryMessageDetail?: {
			afterReservationMessage?: string;
			reminderMessageSevenDays?: string;
			reminderMessageOneDay?: string;
		};
		cancelConditions?: CancelCondition[];
		cancelDescription?: string | null;
		bookCancelMessage?: string | null;
		isDisplayed?: boolean;
		notRegisterEventSameTime?: boolean;
		isProgram?: boolean;
	};
	occurrences: Array<{
		eventOccurrenceId: number;
		eventOccasionId: number;
		startAt: string | Date;
		endAt: string | Date;
		maxAttendees: number;
		currentExpectedCount?: number;
		currentConfirmedCount?: number;
		remarks?: string | null;
		isDisplayed?: boolean;
		createdAt?: string;
		updatedAt?: string;
	}>;
	cancellationPolicies?: Array<unknown>;
}

export const getEventDetail = async (
	eventOccasionId: number,
	_startDate?: string,
	_endDate?: string,
): Promise<EventDetailData> => {
	const response = await fetch(`${API_BASE}/occasion/${eventOccasionId}`, {
		method: "GET",
		headers: { "Content-Type": "application/json" },
		credentials: "include",
	});

	if (!response.ok) {
		const message = await response.text();
		throw new Error(message || `Request failed: ${response.status}`);
	}

	const data: OccasionApiResponse = await response.json();

	// Ensure occurrences is an array
	const occurrencesArray = Array.isArray(data.occurrences)
		? data.occurrences
		: [];

	// Transform API response to EventDetailData
	const totalExpected = occurrencesArray.reduce(
		(sum, occ) => sum + (occ.currentExpectedCount || 0),
		0,
	);
	const totalConfirmed = occurrencesArray.reduce(
		(sum, occ) => sum + (occ.currentConfirmedCount || 0),
		0,
	);
	const totalMaxAttendees = occurrencesArray.reduce(
		(sum, occ) => sum + (occ.maxAttendees || 0),
		0,
	);

	const occurrences: EventOccurrence[] = occurrencesArray.map((occ) => {
		const startAt =
			occ.startAt instanceof Date
				? occ.startAt.toISOString()
				: typeof occ.startAt === "string"
					? occ.startAt
					: new Date(occ.startAt).toISOString();
		const endAt =
			occ.endAt instanceof Date
				? occ.endAt.toISOString()
				: typeof occ.endAt === "string"
					? occ.endAt
					: new Date(occ.endAt).toISOString();

		return {
			occurrenceId: occ.eventOccurrenceId,
			eventOccasionId: occ.eventOccasionId,
			startAt,
			endAt,
			maxAttendees: occ.maxAttendees || 0,
			sumExpected: occ.currentExpectedCount || 0,
			sumAttended: occ.currentConfirmedCount || 0,
			remarks: occ.remarks || null,
			isDisplayed: occ.isDisplayed ?? true,
			createdAt: occ.createdAt,
			updatedAt: occ.updatedAt,
		};
	});

	// Determine the event fee/price - use fee if available, otherwise parse price string
	let eventFee = data.occasion.fee;
	if ((!eventFee || eventFee === 0) && data.occasion.price) {
		// price can be a string like "34343.00" or a number
		const parsedPrice =
			typeof data.occasion.price === "string"
				? Number.parseFloat(data.occasion.price)
				: data.occasion.price;
		if (!Number.isNaN(parsedPrice) && parsedPrice > 0) {
			eventFee = parsedPrice;
		}
	}

	return {
		eventOccasionId: data.occasion.eventOccasionId,
		title: data.occasion.title,
		subtitle: data.occasion.subtitle,
		description: data.occasion.description,
		heroImageUrl: data.occasion.heroImageUrl,
		gallery: data.occasion.gallery || [],
		isPublished: data.occasion.isPublished,
		reservationCount: totalExpected,
		attendeeCount: totalConfirmed,
		maximumAttendees: totalMaxAttendees,
		occurrences,
		fee: eventFee,
		cancelable: Boolean(data.occasion.cancelable),
		cancelConditions: data.occasion.cancelConditions || [],
		cancelDescription: data.occasion.cancelDescription,
		bookCancelMessage: data.occasion.bookCancelMessage,
		isDisplayed: Boolean(data.occasion.isDisplayed),
		isMultiEvent: Boolean(
			data.occasion.isMultiEvent ?? data.occasion.allowMultipleBookings,
		),
		allowMultipleBookings: Boolean(
			data.occasion.allowMultipleBookings ?? data.occasion.isMultiEvent,
		),
		isMessage: Boolean(data.occasion.isMessage),
		isCheckin: Boolean(data.occasion.isCheckin),
		notRegisterEventSameTime: Boolean(data.occasion.notRegisterEventSameTime),
		isProgram: Boolean(data.occasion.isProgram),
		membershipScope: data.occasion.membershipScope,
		categoryMessageDetail: data.occasion.categoryMessageDetail,
		adminNotificationMemberIds: data.occasion.adminNotificationMemberIds,
		occasionImages: data.occasion.gallery?.map((url, index) => ({
			picUrl: url,
			showOrder: index,
		})),
	};
};

export const createManualReservation = async (
	payload: ManualReservationPayload,
): Promise<{ success: boolean; registrationId: number }> => {
	const response = await fetch(`${API_BASE}/registration/manual`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		credentials: "include",
		body: JSON.stringify(payload),
	});

	if (!response.ok) {
		const message = await response.text();
		throw new Error(message || `Request failed: ${response.status}`);
	}

	return response.json();
};

export const createOccurrences = async (
	payload: CreateOccurrencePayload,
): Promise<{ success: boolean; created: number }> => {
	const response = await fetch(`${API_BASE}/occurrence/bulk`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		credentials: "include",
		body: JSON.stringify(payload),
	});

	if (!response.ok) {
		const message = await response.text();
		throw new Error(message || `Request failed: ${response.status}`);
	}

	return response.json();
};

export const deleteOccurrence = async (
	occurrenceId: number,
): Promise<{ success: boolean }> => {
	const response = await fetch(`${API_BASE}/occurrence/${occurrenceId}`, {
		method: "DELETE",
		headers: { "Content-Type": "application/json" },
		credentials: "include",
	});

	if (!response.ok) {
		const message = await response.text();
		throw new Error(message || `Request failed: ${response.status}`);
	}

	return response.json();
};

export const deleteOccurrencesBatch = async (
	payload: DeleteOccurrencesPayload,
): Promise<{ success: boolean; deleted: number }> => {
	const response = await fetch(`${API_BASE}/occurrence/bulk`, {
		method: "DELETE",
		headers: { "Content-Type": "application/json" },
		credentials: "include",
		body: JSON.stringify(payload),
	});

	if (!response.ok) {
		const message = await response.text();
		throw new Error(message || `Request failed: ${response.status}`);
	}

	return response.json();
};

export const exportReservations = async (
	params: ReservationExportParams,
): Promise<Blob> => {
	const searchParams = new URLSearchParams();
	searchParams.set("eventOccasionId", String(params.eventOccasionId));
	if (params.startDate) searchParams.set("startDate", params.startDate);
	if (params.endDate) searchParams.set("endDate", params.endDate);
	if (params.format) searchParams.set("format", params.format);

	const response = await fetch(
		`${API_BASE}/registration/export?${searchParams.toString()}`,
		{
			method: "GET",
			credentials: "include",
		},
	);

	if (!response.ok) {
		const message = await response.text();
		throw new Error(message || `Request failed: ${response.status}`);
	}

	return response.blob();
};

export const getPublicHolidays = async (): Promise<Record<string, string>> => {
	const response = await fetch("/api/public/holidays", {
		method: "GET",
		headers: { "Content-Type": "application/json" },
	});

	if (!response.ok) {
		return {};
	}

	return response.json();
};

export interface OccurrenceDetailResponse {
	occurrenceId: number;
	eventOccasionId: number;
	startAt: string;
	endAt: string;
	maxAttendees: number;
	sumExpected: number;
	sumAttended: number;
	remarks?: string | null;
	isDisplayed: boolean;
	registrations: Registration[];
	cancels: Registration[];
}

export const getOccurrenceDetail = async (
	occurrenceId: number | undefined,
): Promise<OccurrenceDetailResponse> => {
	if (!occurrenceId) {
		throw new Error("occurrenceId is required");
	}
	const response = await fetch(`${API_BASE}/occurrence/${occurrenceId}`, {
		method: "GET",
		headers: { "Content-Type": "application/json" },
		credentials: "include",
	});

	if (!response.ok) {
		const message = await response.text();
		throw new Error(message || `Request failed: ${response.status}`);
	}

	const data = await response.json();
	const occ = data.occurrence;

	const normalizeDate = (value: string | Date | undefined | null): string => {
		if (!value) return new Date().toISOString();
		if (value instanceof Date) return value.toISOString();
		if (typeof value === "string") return value;
		return new Date(value).toISOString();
	};

	return {
		occurrenceId: occ?.eventOccurrenceId || occurrenceId || 0,
		eventOccasionId: occ?.eventOccasionId,
		startAt: normalizeDate(occ?.startAt),
		endAt: normalizeDate(occ?.endAt),
		maxAttendees: occ?.maxAttendees || 0,
		sumExpected:
			occ?.currentExpectedCount ||
			data.registrationSummary?.byStatus?.pending?.expectedTotal ||
			0,
		sumAttended:
			occ?.currentConfirmedCount ||
			data.registrationSummary?.byStatus?.confirmed?.expectedTotal ||
			0,
		remarks: occ?.remarks,
		isDisplayed: occ?.isDisplayed ?? true,
		registrations: occ?.registrations || [],
		cancels: occ?.cancels || [],
	};
};

export const cancelRegistration = async (
	registrationId: number,
): Promise<{ success: boolean }> => {
	const response = await fetch(
		`${API_BASE}/registration/${registrationId}/status`,
		{
			method: "PUT",
			headers: { "Content-Type": "application/json" },
			credentials: "include",
			body: JSON.stringify({ status: "cancelled" }),
		},
	);

	if (!response.ok) {
		const message = await response.text();
		throw new Error(message || `Request failed: ${response.status}`);
	}

	return response.json();
};

export const updateOccurrenceVisibility = async (
	occurrenceId: number,
	isDisplayed: boolean,
): Promise<void> => {
	const response = await fetch(
		`${API_BASE}/occurrence/${occurrenceId}/display`,
		{
			method: "PATCH",
			headers: { "Content-Type": "application/json" },
			credentials: "include",
			body: JSON.stringify({ isDisplayed }),
		},
	);

	if (!response.ok) {
		const message = await response.text();
		throw new Error(message || `Request failed: ${response.status}`);
	}
};

export const getMemberDetail = async (
	memberId: number,
): Promise<MemberDetail> => {
	const response = await fetch(`/api/m/event/member/${memberId}/detail`, {
		method: "GET",
		headers: { "Content-Type": "application/json" },
		credentials: "include",
	});

	if (!response.ok) {
		const message = await response.text();
		throw new Error(message || `Request failed: ${response.status}`);
	}

	return response.json();
};

export const getChats = async (memberId: number): Promise<ChatMessage[]> => {
	const response = await fetch(`/api/m/members/chats/${memberId}`, {
		method: "GET",
		headers: { "Content-Type": "application/json" },
		credentials: "include",
	});

	if (!response.ok) {
		const message = await response.text();
		throw new Error(message || `Request failed: ${response.status}`);
	}

	return response.json();
};

export const sendChat = async (
	memberId: number,
	contents: string,
): Promise<string> => {
	const response = await fetch(`/api/m/members/chats/${memberId}`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		credentials: "include",
		body: JSON.stringify({ contents }),
	});

	if (!response.ok) {
		const message = await response.text();
		throw new Error(message || `Request failed: ${response.status}`);
	}

	return response.text();
};

export const updateRegistration = async (
	registrationId: number,
	data: UpdateRegistrationPayload,
): Promise<Registration> => {
	const response = await fetch(`${API_BASE}/registration/${registrationId}`, {
		method: "PUT",
		headers: { "Content-Type": "application/json" },
		credentials: "include",
		body: JSON.stringify(data),
	});

	if (!response.ok) {
		const message = await response.text();
		throw new Error(message || `Request failed: ${response.status}`);
	}

	return response.json();
};

export const updateOccurrence = async (
	occurrenceId: number,
	data: UpdateOccurrencePayload,
): Promise<EventOccurrence> => {
	const response = await fetch(`${API_BASE}/occurrence/${occurrenceId}`, {
		method: "PUT",
		headers: { "Content-Type": "application/json" },
		credentials: "include",
		body: JSON.stringify(data),
	});

	if (!response.ok) {
		const message = await response.text();
		throw new Error(message || `Request failed: ${response.status}`);
	}

	return response.json();
};

export const updateRegistrationStatus = async (
	registrationId: number,
	payload: UpdateRegistrationStatusPayload,
): Promise<{ success: boolean }> => {
	const response = await fetch(
		`${API_BASE}/registration/${registrationId}/status`,
		{
			method: "PUT",
			headers: { "Content-Type": "application/json" },
			credentials: "include",
			body: JSON.stringify(payload),
		},
	);

	if (!response.ok) {
		const message = await response.text();
		throw new Error(message || `Request failed: ${response.status}`);
	}

	return response.json();
};

export const recordAttendance = async (
	registrationId: number,
	payload: UpdateAttendancePayload,
): Promise<Registration> => {
	const response = await fetch(
		`${API_BASE}/registration/${registrationId}/attendance`,
		{
			method: "POST",
			headers: { "Content-Type": "application/json" },
			credentials: "include",
			body: JSON.stringify(payload),
		},
	);

	if (!response.ok) {
		const message = await response.text();
		throw new Error(message || `Request failed: ${response.status}`);
	}

	return response.json();
};
